<template>
  <div class="content-wrapper vh-100">
    <SmsAuthWithPhoneSwitch
      v-if="isSms"
      :title="headerTitle"
      :phoneNumber="phoneNumber"
      :createSms="createSms"
      :validateSms="validateSms"
      :createPhoneRequest="createPhoneRequest"
      @switchToVoice="switchProvider('phone')"
      @validated="onValidated"
    />
    <PhoneAuth
      v-else-if="isVoice"
      :title="headerTitle"
      :phoneNumber="phoneNumber"
      :createPhoneRequest="createPhoneRequest"
      :validatePhoneRequest="validatePhoneRequest"
      @switchToSMS="switchProvider('sms')"
      @validated="onValidated"
    />
  </div>
  <ReactivateAccountModal
    :isShown="isShownReactivateAccountModal"
    @ok="reactive"
    @close="forceNavigateWelcome"
  />
  <ConfirmBirthDateModal
    :isShownInputModal="isShownBirthDateModal"
    :isShownIncorrectModal="isShownIncorrectBirthDateModal"
    @ok="confirmBirthDate"
    @close="forceNavigateWelcome"
  />
  <ConfirmAccountModal
    v-if="foundUser"
    :isShown="isShownConfirmAccountModal"
    :user="foundUser"
    @ok="navigatePasswordSetting"
    @close="forceNavigateWelcome"
  />
</template>

<script lang="ts">
import { Component, Vue } from "vue-facing-decorator";
import { authService } from "@/app/services/AuthService";
import { smsService } from "@/app/services/SmsService";
import { ProviderType } from "@/const/auth";
import { authStore } from "@/store/auth";
import ConfirmAccountModal from "@/components/organisms/auth/ConfirmAccountModal.vue";
import ConfirmBirthDateModal from "@/components/organisms/auth/ConfirmBirthDateModal.vue";
import ReactivateAccountModal from "@/components/organisms/auth/ReactivateAccountModal.vue";
import SmsAuthWithPhoneSwitch from "@/components/organisms/auth/SmsAuthWithPhoneSwitch.vue";
import PhoneAuth from "@/components/organisms/auth/PhoneAuth.vue";
import { CreateSmsArgs } from "@/types/auth/CreateSms";
import { ValidateSmsArgs } from "@/types/auth/ValidateSms";
import { CreatePhoneRequestArgs } from "@/types/auth/CreatePhoneRequest";
import { ValidatePhoneRequestArgs } from "@/types/auth/ValidatePhoneRequest";
import { sendAdjustEvent } from "@/plugins/adjust";
import { sendFirebaseCustomEvent } from "@/plugins/firebase";
import { FoundUser } from "@/types/auth/RestartVerification";
import { routerReplace } from "@/utils/routerReplace";
import { showErrorModal } from "@/utils/showErrorModal";
import { routerPush } from "@/utils/routerPush";
import { AbTest3113 } from "@/app/services/AbTestService";

@Component({
  components: {
    SmsAuthWithPhoneSwitch,
    PhoneAuth,
    ConfirmAccountModal,
    ConfirmBirthDateModal,
    ReactivateAccountModal
  }
})
export default class PhoneNumberAuth extends Vue {
  providerType: ProviderType = "sms";
  isShownReactivateAccountModal = false;
  isShownBirthDateModal = false;
  isShownConfirmAccountModal = false;
  isShownIncorrectBirthDateModal = false;
  foundUser: FoundUser | null = null;

  get phoneNumber() {
    return authStore.temporaryPhoneNumber;
  }

  get isSms() {
    return this.providerType === "sms";
  }

  get isVoice() {
    return this.providerType === "phone";
  }

  get headerTitle() {
    return "電話番号認証";
  }

  createSms(body: CreateSmsArgs) {
    return smsService.createSms(body);
  }

  validateSms(body: ValidateSmsArgs) {
    return smsService.validateSms(body);
  }

  createPhoneRequest(body: CreatePhoneRequestArgs) {
    return smsService.createPhoneRequest(body);
  }

  validatePhoneRequest(body: ValidatePhoneRequestArgs) {
    return smsService.validatePhoneRequest(body);
  }

  switchProvider(provider: ProviderType) {
    this.providerType = provider;
  }

  onValidated(smsSessionId: string) {
    AbTest3113.registerEvent(AbTest3113.eventTypes.enterCodeNext);
    this.transitionByLoginStatus(smsSessionId);
  }

  transitionByLoginStatus(smsSessionId: string) {
    switch (authStore.workerLoginStatus) {
      // pushだとbackされたときに毎度smsが飛ぶため、この画面にはbackできないようにreplace
      case "v1_user":
        this.$router.replace({ name: "V1UserAccountAuth" });
        break;
      case "verified_leave_user":
      case "unverified_leave_user":
        sendAdjustEvent("FINISH_INPUT_SMS_CODE");
        sendFirebaseCustomEvent("finish_input_sms_code", {
          session_id: authStore.sessionId || "unknown"
        });
        authStore.changeSmsSessionId(smsSessionId);
        this.showReactivateAccountModal();
        break;
      case "new_user":
        sendAdjustEvent("FINISH_INPUT_SMS_CODE");
        sendFirebaseCustomEvent("finish_input_sms_code", {
          session_id: authStore.sessionId || "unknown"
        });
        authStore.changeSmsSessionId(smsSessionId);
        this.$router.replace({ name: "PasswordSetting" });
        break;
    }
  }

  showReactivateAccountModal() {
    this.isShownReactivateAccountModal = true;
  }

  hideReactivateAccountModal() {
    this.isShownReactivateAccountModal = false;
  }

  showBirthDateModal() {
    this.isShownBirthDateModal = true;
  }

  hideBirthDateModal() {
    this.isShownBirthDateModal = false;
  }

  showConfirmAccountModal() {
    this.isShownConfirmAccountModal = true;
  }

  hideConfirmAccountModal() {
    this.isShownConfirmAccountModal = false;
  }

  reactive() {
    if (authStore.workerLoginStatus === "unverified_leave_user") {
      this.navigatePasswordSetting();
      return;
    }
    this.hideReactivateAccountModal();
    this.showBirthDateModal();
  }

  async confirmBirthDate(birthDate: string) {
    try {
      this.$loading.show();
      const response = await authService.verifyReactivateWorkers({
        smsSessionId: authStore.smsSessionId!,
        birthDate
      });
      if (!response.found) {
        this.showIncorrectBirthDateModal();
        return;
      }
      this.foundUser = response.user;
      this.hideBirthDateModal();
      this.showConfirmAccountModal();
    } catch (e) {
      await showErrorModal(this, e as Error);
    } finally {
      this.$loading.hide();
    }
  }

  showIncorrectBirthDateModal() {
    this.isShownIncorrectBirthDateModal = true;
  }

  forceNavigateWelcome() {
    routerReplace(this, { name: "Welcome" });
  }

  navigatePasswordSetting() {
    routerPush(this, { name: "PasswordSetting" });
  }
}
</script>

<style lang="scss" scoped>
.content-wrapper {
  background-color: #f2f2f2;

  :deep(.code-input) {
    background-color: $white;
  }
}
</style>
